<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

/**
 * Tarjeta de cifra.
 *
 * Existe como componente y no como un `div` repetido porque el problema que
 * resuelve es de jerarquía, y la jerarquía se rompe en cuanto se copia a mano
 * unas pocas veces: la etiqueta y el dato compiten en el mismo plano, el icono
 * flota sin anclar nada y el conjunto no dice qué periodo mide.
 *
 * El orden de lectura es deliberado y va de menos a más peso:
 *
 * 1. **Etiqueta**, pequeña y apagada. Dice qué es, no cuánto.
 * 2. **Dato**, grande y con cifras tabulares. Es lo que se viene a mirar.
 * 3. **Contexto**, pequeño y apagado debajo. Dice de cuándo es, o cómo se
 *    calcula, para que el número no quede suelto.
 *
 * El icono va en un cuadrado de color en vez de suelto: así ancla la esquina de
 * la tarjeta y da un punto de partida a la mirada en una rejilla de cuatro.
 *
 * El color del `tone` marca de qué va la tarjeta, no decora: si todo va en el
 * color de marca, el color no distingue nada.
 */

type Tone = 'brand' | 'success' | 'info' | 'warn' | 'danger' | 'neutral'

interface Props {
  label: string
  value: string | number
  /** Qué periodo mide o cómo se calcula. Sin esto el número va suelto. */
  context?: string
  icon?: string
  tone?: Tone
  /** Ruta a la que lleva la tarjeta. Sin ella, la tarjeta no es pulsable. */
  to?: string
  /** Segunda línea de contexto, para cuando `context` no basta. */
  footnote?: string
}

const props = withDefaults(defineProps<Props>(), {
  context: undefined,
  icon: undefined,
  tone: 'brand',
  to: undefined,
  footnote: undefined,
})

const router = useRouter()

const isInteractive = computed(() => props.to !== undefined)

const TONES: Record<Tone, { chip: string; value: string; bar: string }> = {
  brand: {
    chip: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200',
    value: 'text-surface-900 dark:text-surface-100',
    bar: 'bg-primary-500',
  },
  success: {
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    value: 'text-emerald-700 dark:text-emerald-300',
    bar: 'bg-emerald-500',
  },
  info: {
    chip: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    value: 'text-sky-700 dark:text-sky-300',
    bar: 'bg-sky-500',
  },
  warn: {
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    value: 'text-amber-700 dark:text-amber-300',
    bar: 'bg-amber-500',
  },
  danger: {
    chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    value: 'text-red-700 dark:text-red-300',
    bar: 'bg-red-500',
  },
  neutral: {
    chip: 'bg-surface-100 text-surface-600 dark:bg-surface-800 dark:text-surface-300',
    value: 'text-surface-900 dark:text-surface-100',
    bar: 'bg-surface-400',
  },
}

const tone = computed(() => TONES[props.tone])

function open(): void {
  if (props.to !== undefined) {
    void router.push({ name: props.to })
  }
}
</script>

<template>
  <component
    :is="isInteractive ? 'button' : 'div'"
    :type="isInteractive ? 'button' : undefined"
    :data-testid="`stat-${label}`"
    :aria-label="isInteractive ? `${label}: ${value}. Ir a la sección` : undefined"
    class="relative flex h-full w-full flex-col items-start gap-4 overflow-hidden rounded-xl border border-surface-200 bg-white p-5 text-left transition-all dark:border-surface-700 dark:bg-surface-900"
    :class="isInteractive
      ? 'cursor-pointer hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:hover:border-primary-700 dark:focus-visible:ring-offset-surface-900'
      : ''"
    @click="open"
  >
    <!--
      Franja de color en el canto: separa la tarjeta de la siguiente sin
      depender del borde, y es lo primero que se ve al recorrer la rejilla.
    -->
    <span
      :class="['absolute inset-y-0 left-0 w-1', tone.bar]"
      aria-hidden="true"
    />

    <div class="flex w-full items-start justify-between gap-3">
      <p class="text-sm font-medium leading-snug text-surface-500 dark:text-surface-400">
        {{ label }}
      </p>

      <span
        v-if="icon"
        :class="['flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tone.chip]"
        aria-hidden="true"
      >
        <i
          :class="icon"
          class="text-lg"
        />
      </span>
    </div>

    <div class="w-full">
      <p
        :class="['text-3xl font-semibold leading-none tracking-tight tabular-nums', tone.value]"
        :data-testid="`stat-value-${label}`"
      >
        {{ value }}
      </p>

      <p
        v-if="context"
        class="mt-2 text-sm leading-snug text-surface-500 dark:text-surface-400"
      >
        {{ context }}
      </p>

      <p
        v-if="footnote"
        class="mt-1 text-xs leading-snug text-surface-400 dark:text-surface-500"
      >
        {{ footnote }}
      </p>
    </div>
  </component>
</template>
